import { useEffect, useMemo, useRef, useState } from "react";
import { Check, Loader2, Pause, Play, RefreshCw, User2 } from "lucide-react";
import type { CuratedVoice } from "@/services/ads/videos/videoDraft.types";
import { previewCuratedVoice } from "@/services/ads/videos/videoDraftService";
import { useCuratedVoices } from "../hooks/useCuratedVoices";
import { dictionary, type Language } from "@/utils/dictionary";
import { useAuth } from "@/context/AuthContext";

// Voice picker for modeling voice-over. Renders the six Gemini curated
// voices as a 2-col grid of cards; each card plays its static compressed
// MP3 sample inline via a single shared <audio> tag. The accent and music
// fields below the grid are persisted right before the final render.

export interface VoiceSettings {
  stability: number;
  similarity: number;
  style: number;
  speed: number;
}

export const DEFAULT_VOICE_SETTINGS: VoiceSettings = {
  stability: 0.45,
  similarity: 0.75,
  style: 0.55,
  speed: 1.1,
};

interface VoiceLibraryProps {
  selectedVoiceId: string | null;
  onSelectVoice: (voice: CuratedVoice) => void;
  preferredGender?: "female" | "male" | string | null;
  settings: VoiceSettings;
  onSettingsChange: (settings: VoiceSettings) => void;
  accentCity: string;
  onAccentCityChange: (city: string) => void;
  musicPrompt: string;
  onMusicPromptChange: (prompt: string) => void;
  /** Disable interaction while an audio generation call is in flight. */
  disabled?: boolean;
  /** Loading skeleton height hint; falls back to auto. */
  minHeight?: number | string;
}

export function VoiceLibrary({
  selectedVoiceId,
  onSelectVoice,
  preferredGender,
  accentCity,
  onAccentCityChange,
  musicPrompt,
  onMusicPromptChange,
  disabled = false,
  minHeight,
}: VoiceLibraryProps) {
  const { language } = useAuth();
  const t = (key: string, fallback?: string) =>
    dictionary[key]?.[language as Language] ?? fallback ?? key;
  const { voices, isLoading, error, refresh } = useCuratedVoices();
  const [playingVoiceId, setPlayingVoiceId] = useState<string | null>(null);
  const [loadingPreviewVoiceId, setLoadingPreviewVoiceId] = useState<string | null>(null);
  const [previewUrls, setPreviewUrls] = useState<Record<string, string>>({});
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const maleVoices = useMemo(() => voices.filter((voice) => voice.gender === "male").slice(0, 3), [voices]);
  const femaleVoices = useMemo(() => voices.filter((voice) => voice.gender === "female").slice(0, 3), [voices]);

  // Single shared audio element — stopping any previous sample before
  // starting a new one keeps the picker from turning into a cacophony if
  // the user hammer-clicks through the grid.
  useEffect(() => {
    const el = new Audio();
    el.addEventListener("ended", () => setPlayingVoiceId(null));
    el.addEventListener("pause", () => setPlayingVoiceId(null));
    audioRef.current = el;
    return () => {
      el.pause();
      audioRef.current = null;
    };
  }, []);

  useEffect(() => {
    if (isLoading || disabled || voices.length === 0) return;
    const selected = voices.find((voice) => voice.voice_id === selectedVoiceId && voice.provider === "gemini");
    if (!selected) {
      const normalizedGender = preferredGender === "female" || preferredGender === "male" ? preferredGender : null;
      const preferredVoice = normalizedGender
        ? voices.find((voice) => voice.provider === "gemini" && voice.gender === normalizedGender)
        : null;
      const defaultVoice =
        voices.find((voice) => voice.provider === "gemini" && voice.voice_id === "Leda") ?? voices[0];
      onSelectVoice(preferredVoice ?? defaultVoice);
    }
  }, [disabled, isLoading, onSelectVoice, preferredGender, selectedVoiceId, voices]);

  const previewCacheKey = (voice: CuratedVoice) => voice.voice_id;

  const playSample = async (voice: CuratedVoice) => {
    const el = audioRef.current;
    if (!el) return;
    if (playingVoiceId === voice.voice_id) {
      el.pause();
      setPlayingVoiceId(null);
      return;
    }
    const cacheKey = previewCacheKey(voice);
    let sampleUrl = previewUrls[cacheKey] || voice.sample_url || "";
    if (!sampleUrl) {
      setLoadingPreviewVoiceId(voice.voice_id);
      const { res } = await previewCuratedVoice(voice.voice_id, {
        accent_city: accentCity.trim() || "Medellín",
      });
      setLoadingPreviewVoiceId(null);
      sampleUrl = res?.sample_url || "";
      if (!sampleUrl) return;
    }
    setPreviewUrls((current) => ({ ...current, [cacheKey]: sampleUrl }));
    el.pause();
    el.src = sampleUrl;
    el.currentTime = 0;
    el.play().catch(() => {
      // autoplay or network error — silent fail, the user can retry
      setPlayingVoiceId(null);
    });
    setPlayingVoiceId(voice.voice_id);
  };

  const skeletons = useMemo(() => Array.from({ length: 6 }, (_, i) => i), []);

  if (error && !isLoading) {
    return (
      <div
        className="rounded-xl border border-destructive/30 bg-destructive/5 p-4 flex items-center justify-between gap-3"
        style={minHeight ? { minHeight } : undefined}
      >
        <p className="text-sm text-foreground/80 leading-relaxed">
          {t("video-voice-library-error", "No pudimos cargar las voces. Intenta de nuevo.")}
        </p>
        <button
          type="button"
          onClick={refresh}
          className="inline-flex items-center gap-1.5 text-xs font-medium text-primary hover:underline"
        >
          <RefreshCw className="h-3.5 w-3.5" />
          {t("video-voice-library-retry", "Reintentar")}
        </button>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="grid grid-cols-2 gap-2" style={minHeight ? { minHeight } : undefined}>
        {skeletons.map((i) => (
          <div
            key={i}
            className="rounded-xl border border-border/40 bg-muted/30 animate-pulse h-[76px]"
          />
        ))}
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {/* Voice grid. Each card is a <div role="radio"> (not a <button>) so
          the inline "play sample" <button> is a valid DOM descendant — the
          old <button>-inside-<button> nesting was throwing a React warning
          and breaking accessibility semantics. Keyboard support kept via
          role + onKeyDown for Enter/Space. */}
      <div
        role="radiogroup"
        aria-label={t("video-voice-library-aria", "Elige la voz del video")}
        className="grid grid-cols-2 gap-2"
      >
        {[maleVoices, femaleVoices].map((group, groupIndex) => (
          <div key={groupIndex === 0 ? "male" : "female"} className="space-y-2">
            {group.map((voice) => {
              const selected = voice.voice_id === selectedVoiceId;
              const playing = voice.voice_id === playingVoiceId;
              const loadingPreview = voice.voice_id === loadingPreviewVoiceId;
              const handleSelect = () => {
                if (!disabled) onSelectVoice(voice);
              };
              return (
                <div
                  key={voice.voice_id}
                  role="radio"
                  aria-checked={selected}
                  aria-disabled={disabled}
                  tabIndex={disabled ? -1 : 0}
                  onClick={handleSelect}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      handleSelect();
                    }
                  }}
                  className={[
                    "group relative rounded-xl border text-left p-2.5 transition-all",
                    "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40",
                    selected
                      ? "border-primary/60 bg-gradient-to-br from-primary/5 to-accent/5 shadow-sm"
                      : "border-border/50 bg-white hover:border-primary/40 hover:shadow-sm",
                    disabled ? "opacity-60 cursor-not-allowed" : "cursor-pointer",
                  ].join(" ")}
                >
                  {selected && (
                    <div className="absolute top-2 right-2 h-4 w-4 rounded-full bg-primary text-white flex items-center justify-center">
                      <Check className="h-3 w-3" strokeWidth={3} />
                    </div>
                  )}
                  <div className="flex items-center gap-2.5">
                    <div
                      className={[
                        "h-9 w-9 rounded-full flex items-center justify-center shrink-0",
                        voice.gender === "female"
                          ? "bg-pink-100 text-pink-600"
                          : "bg-blue-100 text-blue-600",
                      ].join(" ")}
                      aria-hidden="true"
                    >
                      <User2 className="h-4 w-4" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-semibold text-foreground leading-tight truncate">
                        {voice.display_name}
                      </p>
                    </div>
                    <button
                      type="button"
                      aria-label={t("video-voice-preview-aria", "Escuchar muestra de voz")}
                      onClick={(e) => {
                        e.stopPropagation();
                        void playSample(voice);
                      }}
                      disabled={disabled || loadingPreview}
                      className={[
                        "h-7 w-7 rounded-full flex items-center justify-center shrink-0 transition-colors",
                        playing
                          ? "bg-primary text-white"
                          : "bg-muted/60 text-foreground/70 hover:bg-primary/10 hover:text-primary",
                      ].join(" ")}
                    >
                      {loadingPreview ? (
                        <Loader2 className="h-3.5 w-3.5 animate-spin" strokeWidth={2.5} />
                      ) : playing ? (
                        <Pause className="h-3.5 w-3.5" strokeWidth={2.5} />
                      ) : (
                        <Play className="h-3.5 w-3.5 ml-px" strokeWidth={2.5} />
                      )}
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        ))}
      </div>

      <label className="flex flex-col gap-1.5">
        <span className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
          {t("video-voice-accent-city-label", "Ciudad del acento:")}
        </span>
        <input
          type="text"
          value={accentCity}
          onChange={(e) => onAccentCityChange(e.target.value)}
          disabled={disabled}
          placeholder="Medellín"
          className="h-10 rounded-xl border border-border/60 bg-white px-3 text-sm text-foreground outline-none transition-colors placeholder:text-muted-foreground/60 focus:border-primary/50 focus:ring-2 focus:ring-primary/10 disabled:opacity-60"
        />
      </label>

      <label className="flex flex-col gap-1.5">
        <span className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
          {t("video-voice-music-type-label", "Tipo de música:")}
        </span>
        <textarea
          value={musicPrompt}
          onChange={(e) => onMusicPromptChange(e.target.value)}
          disabled={disabled}
          rows={2}
          placeholder={t("video-voice-music-type-placeholder", "Instrumental suave, moderno, sin voces")}
          className="min-h-[68px] resize-none rounded-xl border border-border/60 bg-white px-3 py-2 text-sm leading-relaxed text-foreground outline-none transition-colors placeholder:text-muted-foreground/60 focus:border-primary/50 focus:ring-2 focus:ring-primary/10 disabled:opacity-60"
        />
      </label>

      {/* Phase 2 V9 REV 2 (Apr 19 2026) — "Opciones avanzadas" HIDDEN.
          Gemini 3.1 Flash TTS (the default provider for Fluxi
          voices) does NOT accept a `speakingRate` param — pace is
          controlled through the natural-language `paceHint` that the
          backend prepends to the prompt (see VideoDraftService.
          paceHintForDuration). So the speed slider was dead UI: it
          mutated voice_config but had zero effect on the rendered audio.
          With pace hints + the per-duration word budgets in the director
          + the truncate+fade overflow protection, the audio always
          fits the clip without user intervention. Slider removed to
          avoid the "I dragged it and nothing changed" confusion.
          The VoiceSettings object + onSettingsChange prop are kept so
          the legacy Eleven/Cloud paths (if ever re-enabled) still work. */}
    </div>
  );
}
